// Serialización del cohete a/desde archivo propio (JSON). Ver docs/theory/rocket-model.md.
import type { Attached, Rocket, StackComponent } from './types';

export const FORMAT = 'openrocket-web.rocket';
export const FORMAT_VERSION = 1;

export interface ReadIssue {
  readonly severity: 'error' | 'warning';
  /** Ruta dentro del archivo, p. ej. `stack[1].children[0]`. */
  readonly path: string;
  readonly message: string;
}

export interface ReadResult {
  /** Presente si el archivo es utilizable (puede haber omitido componentes no soportados). */
  readonly rocket: Rocket | undefined;
  readonly issues: readonly ReadIssue[];
}

export function serializeRocket(rocket: Rocket): string {
  return JSON.stringify({ format: FORMAT, version: FORMAT_VERSION, rocket }, null, 2);
}

type Obj = Record<string, unknown>;
type Kind = 'string' | 'number' | 'boolean' | 'array' | 'object';

const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const kindOf = (v: unknown): string => (Array.isArray(v) ? 'array' : v === null ? 'null' : typeof v);

const NOSE_SHAPES = new Set(['conical', 'ogive', 'elliptical', 'parabolic', 'power']);

const SPEC: Record<string, { required: Record<string, Kind>; optional?: Record<string, Kind> }> = {
  'nose-cone': {
    required: { shape: 'string', length: 'number', baseDiameter: 'number', wallThickness: 'number', filled: 'boolean', material: 'string' },
    optional: { powerExponent: 'number', shoulder: 'object' },
  },
  'body-tube': {
    required: { length: 'number', outerDiameter: 'number', wallThickness: 'number', material: 'string', children: 'array' },
    optional: { motorMount: 'object' },
  },
  transition: { required: { length: 'number', foreDiameter: 'number', aftDiameter: 'number', wallThickness: 'number', material: 'string', children: 'array' } },
  'fin-set': {
    required: { finCount: 'number', rootChord: 'number', tipChord: 'number', span: 'number', sweep: 'number', thickness: 'number', material: 'string', position: 'object' },
    optional: { crossSection: 'string' },
  },
  'inner-tube': {
    required: { length: 'number', innerDiameter: 'number', wallThickness: 'number', material: 'string', position: 'object' },
    optional: { motorMount: 'object' },
  },
  parachute: {
    required: { diameter: 'number', dragCoefficient: 'number', packedLength: 'number', material: 'string', deployment: 'object', position: 'object' },
    optional: { lines: 'object' },
  },
  'shock-cord': { required: { length: 'number', cordLength: 'number', material: 'string', position: 'object' } },
  'mass-component': { required: { length: 'number', mass: 'number', position: 'object' } },
  'centering-ring': { required: { length: 'number', outerDiameter: 'number', innerDiameter: 'number', material: 'string', position: 'object' } },
  bulkhead: { required: { length: 'number', outerDiameter: 'number', innerDiameter: 'number', material: 'string', position: 'object' } },
  'engine-block': { required: { length: 'number', outerDiameter: 'number', innerDiameter: 'number', material: 'string', position: 'object' } },
  'launch-lug': { required: { length: 'number', outerDiameter: 'number', wallThickness: 'number', material: 'string', position: 'object' } },
};

const STACK_TYPES = new Set(['nose-cone', 'body-tube', 'transition']);
const CHILD_TYPES = new Set(['fin-set', 'inner-tube', 'parachute', 'shock-cord', 'mass-component', 'centering-ring', 'bulkhead', 'engine-block', 'launch-lug']);

function checkFields(o: Obj, path: string, issues: ReadIssue[]): boolean {
  const type = o['type'] as string;
  const spec = SPEC[type]!;
  let ok = true;
  const need = (field: string, kind: Kind): void => {
    const v = o[field];
    const good = kind === 'number' ? typeof v === 'number' && Number.isFinite(v) : kindOf(v) === kind;
    if (!good) {
      ok = false;
      issues.push({ severity: 'error', path: `${path}.${field}`, message: `Se esperaba ${kind} y se encontró ${kindOf(v)}` });
    }
  };
  if (typeof o['id'] !== 'string') need('id', 'string');
  if (typeof o['name'] !== 'string') need('name', 'string');
  for (const [f, k] of Object.entries(spec.required)) need(f, k);
  for (const [f, k] of Object.entries(spec.optional ?? {})) if (o[f] !== undefined) need(f, k);
  if (o['massOverride'] !== undefined) need('massOverride', 'number');

  if (ok && type === 'nose-cone' && !NOSE_SHAPES.has(o['shape'] as string)) {
    ok = false;
    issues.push({ severity: 'error', path: `${path}.shape`, message: `Forma de ojiva desconocida: ${String(o['shape'])}` });
  }
  if (ok && 'position' in spec.required) {
    const p = o['position'] as Obj;
    if ((p['anchor'] !== 'front' && p['anchor'] !== 'aft') || typeof p['offset'] !== 'number') {
      ok = false;
      issues.push({ severity: 'error', path: `${path}.position`, message: 'Posición inválida (anchor y offset)' });
    }
  }
  if (ok && type === 'fin-set' && o['crossSection'] !== undefined && !['square', 'rounded', 'airfoil'].includes(o['crossSection'] as string)) {
    ok = false;
    issues.push({ severity: 'error', path: `${path}.crossSection`, message: `Sección de aleta desconocida: ${String(o['crossSection'])}` });
  }
  if (ok && (type === 'inner-tube' || type === 'body-tube') && o['motorMount'] !== undefined) {
    const mm = o['motorMount'] as Obj;
    if (mm['overhang'] !== undefined && (typeof mm['overhang'] !== 'number' || !Number.isFinite(mm['overhang']))) {
      ok = false;
      issues.push({ severity: 'error', path: `${path}.motorMount.overhang`, message: 'Voladizo inválido' });
    }
    if (mm['motor'] !== undefined) {
      const m = mm['motor'];
      if (!isObj(m) || typeof m['motorId'] !== 'string' || typeof m['ejectionDelay'] !== 'number') {
        ok = false;
        issues.push({ severity: 'error', path: `${path}.motorMount.motor`, message: 'Motor inválido (motorId y ejectionDelay)' });
      }
    }
  }
  if (ok && type === 'nose-cone' && o['shoulder'] !== undefined) {
    const sh = o['shoulder'] as Obj;
    if (['length', 'diameter', 'thickness'].some((k) => typeof sh[k] !== 'number' || !Number.isFinite(sh[k])) || typeof sh['capped'] !== 'boolean') {
      ok = false;
      issues.push({ severity: 'error', path: `${path}.shoulder`, message: 'Hombro inválido (length, diameter, thickness y capped)' });
    }
  }
  if (ok && type === 'parachute' && o['lines'] !== undefined) {
    const l = o['lines'] as Obj;
    if (['count', 'length'].some((k) => typeof l[k] !== 'number' || !Number.isFinite(l[k])) || typeof l['material'] !== 'string') {
      ok = false;
      issues.push({ severity: 'error', path: `${path}.lines`, message: 'Cuerdas inválidas (count, length y material)' });
    }
  }
  if (ok && type === 'parachute') {
    const d = o['deployment'] as Obj;
    const good = d['event'] === 'apogee' || d['event'] === 'ejection' || (d['event'] === 'altitude' && typeof d['altitude'] === 'number');
    if (!good) {
      ok = false;
      issues.push({ severity: 'error', path: `${path}.deployment`, message: 'Despliegue inválido' });
    }
  }
  return ok;
}

/** El soporte de motor de antes es un tubo interior marcado como portamotor, con el mismo id. */
function migrateLegacyMount(raw: Obj): Obj {
  const { motor, overhang, ...rest } = raw;
  return { ...rest, type: 'inner-tube', motorMount: { ...(overhang === undefined ? {} : { overhang }), ...(motor === undefined ? {} : { motor }) } };
}

function readChildren(list: unknown[], path: string, issues: ReadIssue[]): Attached[] {
  const out: Attached[] = [];
  list.forEach((entry, i) => {
    const p = `${path}.children[${i}]`;
    const raw = isObj(entry) && entry['type'] === 'motor-mount' ? migrateLegacyMount(entry) : entry;
    if (!isObj(raw) || typeof raw['type'] !== 'string') {
      issues.push({ severity: 'error', path: p, message: 'Componente sin tipo' });
      return;
    }
    if (!CHILD_TYPES.has(raw['type'])) {
      issues.push({ severity: 'warning', path: p, message: `Componente no soportado (se omite): ${raw['type']}` });
      return;
    }
    if (checkFields(raw, p, issues)) out.push(raw as unknown as Attached);
  });
  return out;
}

export function deserializeRocket(text: string): ReadResult {
  const issues: ReadIssue[] = [];
  const fail = (message: string): ReadResult => ({ rocket: undefined, issues: [...issues, { severity: 'error', path: '', message }] });

  let doc: unknown;
  try {
    doc = JSON.parse(text);
  } catch {
    return fail('El archivo no es JSON válido');
  }
  if (!isObj(doc) || doc['format'] !== FORMAT) return fail('El archivo no es un cohete de openrocket-web');
  if (typeof doc['version'] !== 'number' || doc['version'] > FORMAT_VERSION) return fail('Versión del archivo no soportada');
  const r = doc['rocket'];
  if (!isObj(r) || typeof r['name'] !== 'string' || !Array.isArray(r['stack'])) return fail('Estructura de cohete inválida');

  const stack: StackComponent[] = [];
  r['stack'].forEach((raw: unknown, i: number) => {
    const path = `stack[${i}]`;
    if (!isObj(raw) || typeof raw['type'] !== 'string') {
      issues.push({ severity: 'error', path, message: 'Componente sin tipo' });
      return;
    }
    if (!STACK_TYPES.has(raw['type'])) {
      issues.push({ severity: 'warning', path, message: `Componente no soportado (se omite): ${raw['type']}` });
      return;
    }
    if (!checkFields(raw, path, issues)) return;
    if (raw['type'] === 'nose-cone') {
      stack.push(raw as unknown as StackComponent);
      return;
    }
    const children = readChildren(raw['children'] as unknown[], path, issues);
    stack.push({ ...(raw as unknown as StackComponent), children } as StackComponent);
  });

  if (issues.some((i) => i.severity === 'error')) return { rocket: undefined, issues };
  return { rocket: { name: r['name'], stack }, issues };
}
